'use client';

import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  ReactNode,
  useMemo,
  useRef,
} from 'react';
import { useRouter, useSearchParams as useNextSearchParams, usePathname } from 'next/navigation';
import { DateRange } from 'react-day-picker';
import useCurrency from './useCurrency';
import { format } from 'date-fns';

// Types for search state
export interface SearchState {
  destination: string | null;
  area: string | null;
  minPrice: number;
  maxPrice: number | null;
  minBeds: number;
  minBaths: number;
  perks: string[];
  dateRange: DateRange;
  guests: number;
  page: number;
  isLoading: boolean;
  sort?: string;
  currency: string; // Store the currency with the state
}

// Action types for the reducer
export type SearchAction =
  | { type: 'SET_DESTINATION'; payload: string | null }
  | { type: 'SET_AREA'; payload: string | null }
  | { type: 'SET_MIN_PRICE'; payload: number }
  | { type: 'SET_MAX_PRICE'; payload: number | null }
  | { type: 'SET_MIN_BEDS'; payload: number }
  | { type: 'SET_MIN_BATHS'; payload: number }
  | { type: 'TOGGLE_PERK'; payload: string }
  | { type: 'SET_PERKS'; payload: string[] }
  | { type: 'SET_DATE_RANGE'; payload: DateRange }
  | { type: 'SET_GUESTS'; payload: number }
  | { type: 'SET_PAGE'; payload: number }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CURRENCY'; payload: string }
  | { type: 'CLEAR_ALL' }
  | { type: 'INITIALIZE_FROM_URL'; payload: Record<string, string> }
  | { type: 'SET_SORT'; payload: string };

// Initial state
const initialState: SearchState = {
  destination: null,
  area: null,
  minPrice: 0,
  maxPrice: null,
  minBeds: 0,
  minBaths: 0,
  perks: [],
  dateRange: { from: undefined, to: undefined },
  guests: 1,
  page: 1,
  isLoading: false,
  sort: 'trending',
  currency: 'EGP',
};

// Reducer function
function searchReducer(state: SearchState, action: SearchAction): SearchState {
  switch (action.type) {
    case 'SET_DESTINATION':
      if (action.payload === state.destination) {
        return { ...state, destination: null, area: null, page: 1 };
      }
      return { ...state, destination: action.payload, area: null, page: 1 };
    case 'SET_AREA':
      return { ...state, area: action.payload, page: 1 };
    case 'SET_MIN_PRICE':
      return { ...state, minPrice: action.payload, page: 1 };
    case 'SET_MAX_PRICE':
      return { ...state, maxPrice: action.payload, page: 1 };
    case 'SET_MIN_BEDS':
      return { ...state, minBeds: action.payload, page: 1 };
    case 'SET_MIN_BATHS':
      return { ...state, minBaths: action.payload, page: 1 };
    case 'TOGGLE_PERK': {
      const newPerks = state.perks.includes(action.payload)
        ? state.perks.filter((p) => p !== action.payload)
        : [...state.perks, action.payload];
      return { ...state, perks: newPerks, page: 1 };
    }
    case 'SET_PERKS':
      return { ...state, perks: action.payload, page: 1 };
    case 'SET_DATE_RANGE':
      return { ...state, dateRange: action.payload, page: 1 };
    case 'SET_GUESTS':
      return { ...state, guests: action.payload, page: 1 };
    case 'SET_PAGE':
      return { ...state, page: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_CURRENCY':
      return { ...state, currency: action.payload };
    case 'CLEAR_ALL':
      return { ...initialState, page: 1 };
    case 'INITIALIZE_FROM_URL':
      return {
        ...state,
        destination: action.payload.destination || null,
        area: action.payload.area || null,
        minPrice: parseInt(action.payload.minPrice) || 0,
        maxPrice: action.payload.maxPrice ? parseInt(action.payload.maxPrice) : null,
        minBeds: parseInt(action.payload.minBeds) || 0,
        minBaths: parseInt(action.payload.minBaths) || 0,
        perks: action.payload.perks ? action.payload.perks.split(',') : [],
        dateRange: {
          from: action.payload.from ? new Date(action.payload.from) : undefined,
          to: action.payload.to ? new Date(action.payload.to) : undefined,
        },
        guests: parseInt(action.payload.guests) || 1,
        page: parseInt(action.payload.page) || 1,
        sort: action.payload.sort || 'trending',
        currency: action.payload.currency || 'EGP',
      };
    case 'SET_SORT':
      return { ...state, sort: action.payload, page: 1 };
    default:
      return state;
  }
}

// Context
interface SearchContextType {
  state: SearchState;
  dispatch: React.Dispatch<SearchAction>;
  updateURL: () => void;
  clearSearch: () => void;
  togglePerk: (_perk: string) => void;
  setDestination: (_destination: string | null) => void;
  setArea: (_area: string | null) => void;
  setMinPrice: (_price: number) => void;
  setMaxPrice: (_price: number | null) => void;
  setMinBeds: (_beds: number) => void;
  setMinBaths: (_baths: number) => void;
  setDateRange: (_range: DateRange) => void;
  setGuests: (_guests: number) => void;
  setPage: (_page: number) => void;
  activeFilterCount: number;
  destinationLabel: string;
  goToExplore: (_params: {
    destination: string | null;
    area: string | null;
    dateRange: DateRange;
    guests: number;
  }) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

// Provider component
interface SearchProviderProps {
  children: ReactNode;
}

export function SearchProvider({ children }: SearchProviderProps) {
  const [state, dispatch] = useReducer(searchReducer, initialState);
  const router = useRouter();
  const searchParams = useNextSearchParams();
  const isInitialized = useRef(false);
  const isUpdatingURL = useRef(false);
  const { currency } = useCurrency();

  const currentPath = usePathname();

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (state.destination) count++;
    if (state.area) count++;
    if (state.minPrice > 0) count++;
    if (state.maxPrice) count++;
    if (state.minBeds > 0) count++;
    if (state.minBaths > 0) count++;
    if (state.perks.length > 0) count++;
    if (state.guests > 1) count++;
    if (state.page > 1) count++;
    if (state.dateRange.from) count++;
    if (state.dateRange.to) count++;
    return count;
  }, [state]);

  const destinationLabel = useMemo(() => {
    const destination = state.destination;
    switch (destination) {
      case 'north-coast':
        return 'North Coast';
      case 'ain-sokhna':
        return 'Ain Sokhna';
      case 'east-cairo':
        return 'East Cairo';
      case 'west-cairo':
        return 'West Cairo';
      default:
        return 'Egypt';
    }
  }, [state.destination]);

  // Initialize from URL params on mount only
  useEffect(() => {
    if (!isInitialized.current) {
      console.log('initialize from url', searchParams);
      const urlParams = Object.fromEntries(searchParams.entries());
      dispatch({ type: 'INITIALIZE_FROM_URL', payload: urlParams });
      isInitialized.current = true;
    }
  }, [searchParams]);

  useEffect(() => {
    if (JSON.stringify(state) !== JSON.stringify(initialState)) {
      updateURL();
    }
  }, [state]);

  // Sync currency from context to state
  useEffect(() => {
    if (currency !== state.currency) {
      dispatch({ type: 'SET_CURRENCY', payload: currency });
      dispatch({ type: 'SET_MIN_PRICE', payload: 0 });
      dispatch({ type: 'SET_MAX_PRICE', payload: null });
    }
  }, [currency]);

  const updateURL = () => {
    if (isUpdatingURL.current) return;
    if (!currentPath.startsWith('/explore')) return;

    isUpdatingURL.current = true;
    const params = new URLSearchParams();

    if (state.destination) params.set('destination', state.destination);
    if (state.area) params.set('area', state.area);
    if (state.minPrice > 0) params.set('minPrice', state.minPrice.toString());
    if (state.maxPrice) params.set('maxPrice', state.maxPrice.toString());
    if (state.minBeds > 0) params.set('minBeds', state.minBeds.toString());
    if (state.minBaths > 0) params.set('minBaths', state.minBaths.toString());
    if (state.perks.length > 0) params.set('perks', state.perks.join(','));
    if (state.dateRange.from) params.set('from', format(state.dateRange.from, 'yyyy-MM-dd'));
    if (state.dateRange.to) params.set('to', format(state.dateRange.to, 'yyyy-MM-dd'));
    if (state.guests > 1) params.set('guests', state.guests.toString());
    if (state.page > 1) params.set('page', state.page.toString());

    // Add currency to URL if not EGP
    if (state.currency !== 'EGP') {
      params.set('currency', state.currency);
    }

    const queryString = params.toString();
    const newPath = queryString ? `/explore?${queryString}` : '/explore';

    router.replace(newPath, { scroll: false });

    // Reset the flag after a short delay
    setTimeout(() => {
      isUpdatingURL.current = false;
    }, 100);
  };

  const clearSearch = () => {
    dispatch({ type: 'CLEAR_ALL' });
    router.replace('/explore');
  };

  const togglePerk = (_perk: string) => {
    dispatch({ type: 'TOGGLE_PERK', payload: _perk });
  };

  const setDestination = (_destination: string | null) => {
    dispatch({ type: 'SET_DESTINATION', payload: _destination });
  };

  const setArea = (_area: string | null) => {
    dispatch({ type: 'SET_AREA', payload: _area });
  };

  const setMinPrice = (_price: number) => {
    dispatch({ type: 'SET_MIN_PRICE', payload: _price });
  };

  const setMaxPrice = (_price: number | null) => {
    dispatch({ type: 'SET_MAX_PRICE', payload: _price });
  };

  const setMinBeds = (_beds: number) => {
    dispatch({ type: 'SET_MIN_BEDS', payload: _beds });
  };

  const setMinBaths = (_baths: number) => {
    dispatch({ type: 'SET_MIN_BATHS', payload: _baths });
  };

  const setDateRange = (_range: DateRange) => {
    dispatch({ type: 'SET_DATE_RANGE', payload: _range });
  };

  const setGuests = (_guests: number) => {
    dispatch({ type: 'SET_GUESTS', payload: _guests });
  };

  const setPage = (_page: number) => {
    dispatch({ type: 'SET_PAGE', payload: _page });
  };

  const goToExplore = (params: {
    destination: string | null;
    area: string | null;
    dateRange: DateRange;
    guests: number;
  }) => {
    // Update state - updateURL will handle navigation
    const urlParams = new URLSearchParams();
    if (params.destination) urlParams.set('destination', params.destination);
    if (params.area) urlParams.set('area', params.area);
    if (params.dateRange.from) urlParams.set('from', format(params.dateRange.from, 'yyyy-MM-dd'));
    if (params.dateRange.to) urlParams.set('to', format(params.dateRange.to, 'yyyy-MM-dd'));
    if (params.guests) urlParams.set('guests', params.guests.toString());

    router.push(`/explore?${urlParams.toString()}&currency=${currency}`);
  };

  const contextValue: SearchContextType = {
    state,
    dispatch,
    updateURL,
    clearSearch,
    togglePerk,
    setDestination,
    setArea,
    setMinPrice,
    setMaxPrice,
    setMinBeds,
    setMinBaths,
    setDateRange,
    setGuests,
    setPage,
    activeFilterCount,
    destinationLabel,
    goToExplore,
  };

  return <SearchContext.Provider value={contextValue}>{children}</SearchContext.Provider>;
}

export default function useSearch() {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}
